import React, { useMemo } from "react";
import PropTypes from "prop-types";

import algoliasearch from "algoliasearch/lite";
import i18n from "i18n-react";
import {
  Configure,
  Hits,
  Index,
  InstantSearch,
  Pagination,
  SearchBox,
} from "react-instantsearch";

import Hit from "components/SearchModal/Hit";
import NoResults from "components/SearchModal/NoResults";

import { useSettings } from "hooks/useSettings";

import { promoteSearchIds } from "settings";

import { algolia } from "settings";

const searchClient = algoliasearch(algolia.id, algolia.searchKey);

/** Object to map language based columns to the proper column names in the algolia index. */
const pagesMultilingualKeys = {
  en: {
    title: "title",
    clean_text: "clean_text",
  },
};

// Higher order component to bind the onSelect function and the proper type to the Hit component. Required to prevent: no-unstable-nested-components eslint (which prevents unnecessary renders).
const getPreparedHitComponent =
  (onSelect, type, langFile = "en") =>
  (props) => {
    let translationKeys = pagesMultilingualKeys.en;
    if (type === "pages") {
      translationKeys =
        pagesMultilingualKeys[langFile] || pagesMultilingualKeys.en;
    }
    return (
      <Hit
        {...props}
        type={type}
        onSelect={onSelect}
        pagesTranslationKeys={translationKeys}
      />
    );
  };
/**
 * @typedef {object} IndexSearchProps
 * @property {string} algoliaId An algolia search id. See algolia dashboard.
 * @property {string} algoliaSearchKey An algolia search key. See algolia dashboard.
 * @property {Function} onHide A function to call when the modal is closed.
 * @property {string} [defaultQuery=''] The default search query. Defaults to an empty string. Default is `''`
 * @property {Function} [onQuery=null] A function to call when the search query is updated. Gets passed the current
 *   query. Default is `null`
 * @property {boolean} [pages=false] Whether to search pages. Defaults to false. Default is `false`
 * @property {boolean} [salesCompanion=false] Whether to search sales companion items. Defaults to false. Default is `false`
 */

/**
 * A specific implementation/configuration that allows for searching a desired subapp.
 *
 * @param {IndexSearchProps} props Props
 * @returns {JSX.Element} A search component, specifically configured for searching within specific tests.
 */
const IndexSearch = ({
  onHide,
  defaultQuery,
  onQuery = null,
  langFile = "en",
  pages = false,
  salesCompanion = false,
  show,
}) => {
  const {
    appId,
    //lang: { langFile },
    loaded: settingsLoaded,
  } = useSettings();
  const indexName = "pages";

  const { hitsPerPage, attributesToSnippet: attributesToSnippetSetting } =
    algolia;

  // configures params based on langFile
  const { titleColumn, cleanTextColumn, attributesToSnippet } = useMemo(() => {
    const snippetAttributes = attributesToSnippetSetting.map(
      (attribute) => pagesMultilingualKeys[langFile][attribute] || attribute
    );

    return {
      titleColumn: pagesMultilingualKeys[langFile].title,
      cleanTextColumn: pagesMultilingualKeys[langFile].clean_text,
      attributesToSnippet: snippetAttributes,
    };
  }, [attributesToSnippetSetting, langFile]);

  const constructFilters = () => {
    let filters = `app_id = ${appId}`;
    filters += " AND archived = 0";
    filters += " AND type_id != 5";
    filters += " AND NOT title:hidden";
    filters += " AND graduated_unlock_only != 1";
    return filters;
  };

  const constructFiltersSC = () => {
    const filterAppId =
      promoteSearchIds[langFile] ?? promoteSearchIds.default ?? appId;
    let filters = `app_id = ${filterAppId}`;
    filters += " AND graduated_unlock_only != 1";
    return filters;
  };

  const queryHook = (searchQuery, search) => {
    if (onQuery) onQuery(searchQuery);
    search(searchQuery);
  };

  const PreparedSalesCompanionHit = useMemo(
    () => getPreparedHitComponent(onHide, "sales_companion_items"),
    [onHide]
  );

  const PreparedPagesHit = useMemo(
    () => getPreparedHitComponent(onHide, indexName, langFile),
    [langFile, onHide]
  );

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      initialUiState={{
        pages: {
          query: defaultQuery,
        },
        sales_companion_items: {
          query: defaultQuery,
        },
      }}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <SearchBox
        autoFocus
        placeholder={i18n.translate("nav.search")}
        queryHook={queryHook}
      />
      {/* Rendered conditionally: index does not seem to update correctly when the Configures filter prop is updated */}
      {settingsLoaded && (
        <div className="search__container">
          {pages && (
            <Index indexName="pages">
              <h3>Pages</h3>
              <Configure
                filters={constructFilters()}
                hitsPerPage={hitsPerPage}
                attributesToSnippet={attributesToSnippet}
                restrictSearchableAttributes={[titleColumn, cleanTextColumn]}
              />
              <NoResults>
                <Hits hitComponent={PreparedPagesHit} />
              </NoResults>
              <Pagination />
            </Index>
          )}
          <br />
          <hr />
          {salesCompanion && (
            <Index indexName="sales_companion_items">
              <h3>Resources</h3>
              <Configure
                filters={constructFiltersSC()}
                hitsPerPage={hitsPerPage}
                attributesToSnippet={attributesToSnippetSetting}
              />
              <NoResults>
                <Hits hitComponent={PreparedSalesCompanionHit} />
              </NoResults>
              <Pagination />
            </Index>
          )}
        </div>
      )}
    </InstantSearch>
  );
};

IndexSearch.propTypes = {
  algoliaId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  hitsPerPage: PropTypes.number,
  attributesToSnippet: PropTypes.arrayOf(PropTypes.string),
  defaultQuery: PropTypes.string,
  onQuery: PropTypes.func,
  onHide: PropTypes.func.isRequired,
  pages: PropTypes.bool,
  salesCompanion: PropTypes.bool,
};

export default IndexSearch;
